import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useMediaQuery } from '../hooks/useMediaQuery';
import ConfirmDialog from './ConfirmDialog';
import { DashboardIcon, UsersIcon, LogoutIcon, SunIcon, MoonIcon, SettingsIcon } from './Icons';

interface MenuItem {
  id: string;
  label: string;
  path: string;
  icon: React.ReactNode;
}

const menuItems: MenuItem[] = [
  { id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon size={18} /> },
  { id: 'clients', label: 'Clientes', path: '/clients', icon: <UsersIcon size={18} /> },
  { id: 'users', label: 'Usuarios', path: '/users', icon: <SettingsIcon size={18} /> },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const APP_VERSION = "v1.3.5";

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const visibleMenuItems = menuItems.filter(item => {
    if (item.id === 'users') {
      return user?.role === 'master';
    }
    return true;
  });

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  const handleNav = (path: string) => {
    if (path === '#') return;
    navigate(path);
    onClose();
  };

  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const handleLogout = () => {
    setLogoutDialogOpen(true);
  };

  const confirmLogout = () => {
    setLogoutDialogOpen(false);
    logout();
  };

  return (
    <>
      {/* Sidebar Desktop */}
      {!isMobile && (
        <aside style={styles.sidebar}>
          <div style={styles.brand}>
            <img src="/logo.png" alt="Unio AI Logo" style={styles.logoImage} />
            <div style={{ ...styles.brandSub, display: 'flex', justifyContent: 'space-between', marginTop: '0.75rem' }}>
              <span>STRATEGY PHASE</span>
              <span style={{ opacity: 0.6 }}>{APP_VERSION}</span>
            </div>
          </div>

          <nav style={styles.nav}>
            {visibleMenuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNav(item.path)}
                style={{
                  ...styles.menuItem,
                  ...(isActive(item.path) ? styles.menuItemActive : {}),
                }}
              >
                <span style={isActive(item.path) ? styles.menuIconActive : styles.menuIcon}>
                  {item.icon}
                </span>
                <span style={styles.menuLabel}>{item.label}</span>
              </button>
            ))}
          </nav>

          <button
            onClick={toggleTheme}
            style={styles.themeItem}
          >
            <span style={styles.menuIcon}>
              {isDark ? <SunIcon size={18} /> : <MoonIcon size={18} />}
            </span>
            <span style={styles.menuLabel}>
              {isDark ? 'Modo claro' : 'Modo oscuro'}
            </span>
          </button>

          <div style={styles.footer}>
            <div style={styles.userInfo}>
              <div style={styles.userAvatar}>{user?.full_name?.charAt(0).toUpperCase() || 'U'}</div>
              <div style={styles.userDetails}>
                <div style={styles.userName}>{user?.full_name || 'Usuario'}</div>
                <div style={styles.userRole}>{user?.role || 'Strategist'}</div>
              </div>
            </div>
            <button onClick={handleLogout} style={styles.logoutBtn}>
              <LogoutIcon size={14} />
              <span>Cerrar sesión</span>
            </button>
          </div>
        </aside>
      )}

      {/* Bottom Nav - Mobile Only */}
      {isMobile && (
        <nav style={styles.bottomNav}>
          {visibleMenuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleNav(item.path)}
              style={{
                ...styles.bottomNavItem,
                ...(isActive(item.path) ? styles.bottomNavItemActive : {}),
              }}
            >
              <span style={styles.bottomNavIcon}>{item.icon}</span>
              <span style={styles.bottomNavLabel}>{item.label}</span>
            </button>
          ))}
          <button
            onClick={toggleTheme}
            style={styles.bottomNavItem}
          >
            <span style={styles.bottomNavIcon}>
              {isDark ? <SunIcon size={18} /> : <MoonIcon size={18} />}
            </span>
            <span style={styles.bottomNavLabel}>
              {isDark ? 'Claro' : 'Oscuro'}
            </span>
          </button>
          <button
            onClick={handleLogout}
            style={styles.bottomNavItem}
          >
            <span style={styles.bottomNavIcon}>
              <LogoutIcon size={18} />
            </span>
            <span style={styles.bottomNavLabel}>Salir</span>
          </button>
        </nav>
      )}

      <ConfirmDialog
        isOpen={logoutDialogOpen}
        title="Cerrar sesión"
        message="¿Estás seguro de que quieres cerrar sesión?"
        confirmText="Salir"
        cancelText="Cancelar"
        onConfirm={confirmLogout}
        onCancel={() => setLogoutDialogOpen(false)}
      />
    </>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.4)',
    zIndex: 99,
  },
  sidebar: {
    width: 'var(--sidebar-width)',
    height: '100vh',
    background: 'var(--bg-sidebar)',
    color: 'var(--text-sidebar)',
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    left: 0,
    top: 0,
    zIndex: 100,
    transition: 'transform 0.3s ease',
  },
  brand: {
    padding: '1.5rem 1.5rem 1rem',
    display: 'flex',
    flexDirection: 'column',
  },
  logoImage: {
    height: '70px',
    width: 'auto',
    objectFit: 'contain',
    alignSelf: 'center',
  },
  brandSub: {
    fontSize: '0.65rem',
    fontWeight: 500,
    color: 'rgba(255,255,255,0.4)',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    marginTop: '0.25rem',
  },
  nav: {
    flex: 1,
    padding: '0 0.75rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.875rem',
    padding: '0.7rem 1rem',
    borderRadius: '6px',
    border: 'none',
    background: 'transparent',
    color: 'var(--text-sidebar)',
    fontSize: '0.85rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s',
    textAlign: 'left',
    width: '100%',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  menuItemActive: {
    background: 'rgba(255,255,255,0.08)',
    color: 'var(--text-sidebar-active)',
    fontWeight: 600,
  },
  menuIcon: {
    width: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.6,
  },
  menuIconActive: {
    width: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 1,
  },
  menuLabel: {
    flex: 1,
  },
  themeItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.875rem',
    padding: '0.7rem 1rem',
    borderRadius: '6px',
    border: 'none',
    background: 'transparent',
    color: 'var(--text-sidebar)',
    fontSize: '0.85rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s',
    textAlign: 'left',
    width: '100%',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    margin: '0 0.75rem 0.5rem',
  },
  footer: {
    padding: '1rem',
    borderTop: '1px solid rgba(255,255,255,0.06)',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '0.75rem',
  },
  userAvatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: 'var(--accent)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: '0.8rem',
    color: '#fff',
  },
  userDetails: {
    flex: 1,
    overflow: 'hidden',
  },
  userName: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#fff',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  userRole: {
    fontSize: '0.7rem',
    color: 'rgba(255,255,255,0.4)',
    textTransform: 'capitalize',
  },
  logoutBtn: {
    width: '100%',
    padding: '0.5rem',
    borderRadius: '6px',
    border: '1px solid rgba(255,255,255,0.1)',
    background: 'transparent',
    color: 'rgba(255,255,255,0.5)',
    fontSize: '0.75rem',
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
  },
  bottomNav: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    height: '72px',
    background: 'var(--bg-sidebar)',
    borderTop: '1px solid rgba(255,255,255,0.06)',
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    zIndex: 100,
    padding: '0 0.5rem',
  },
  bottomNavItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.25rem',
    background: 'transparent',
    border: 'none',
    color: 'var(--text-sidebar)',
    fontSize: '0.65rem',
    fontWeight: 500,
    cursor: 'pointer',
    padding: '0.4rem 0.6rem',
    borderRadius: '8px',
    transition: 'all 0.15s',
    minWidth: '60px',
    opacity: 0.7,
  },
  bottomNavItemActive: {
    color: 'var(--text-sidebar-active)',
    opacity: 1,
    fontWeight: 600,
  },
  bottomNavIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  bottomNavLabel: {
    fontSize: '0.6rem',
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
  },
};

export default Sidebar;
